import React, { useState, useEffect, useCallback } from 'react';
import { 
  TrendingUp, 
  DollarSign, 
  Clock, 
  Settings, 
  Power, 
  ChevronRight, 
  ChevronDown, 
  ChevronUp, 
  Info, 
  BarChart3, 
  Activity, 
  ArrowRight, 
  AlertTriangle,
  CheckCircle,
  Loader2,
  Repeat
} from 'lucide-react';
import Lottie from 'lottie-react';
import InvestmentTiersModal from './InvestmentTiersModal';

interface ArbitrageRobotPageProps {
  currentBtcPrice: number;
  usdtBalance: number;
  robotState: any;
  onUpdateRobotState: (updates: Partial<any>) => void;
  onUpdateBalances: (updates: { usdt_balance?: number, transaction?: any }) => void;
}

interface ArbitrageOpportunity {
  id: string;
  buyExchange: string;
  sellExchange: string;
  pair: string;
  buyPrice: number;
  sellPrice: number;
  profitPercentage: number;
  timestamp: Date;
}

const ArbitrageRobotPage: React.FC<ArbitrageRobotPageProps> = ({
  currentBtcPrice,
  usdtBalance,
  robotState,
  onUpdateRobotState,
  onUpdateBalances
}) => {
  const [showInvestmentTiers, setShowInvestmentTiers] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [isAllocating, setIsAllocating] = useState(false);
  const [allocationAmount, setAllocationAmount] = useState('');
  const [showAllocationForm, setShowAllocationForm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showTradingLogs, setShowTradingLogs] = useState(false);
  
  // Simulated trading logs
  const [tradingLogs, setTradingLogs] = useState<any[]>([]);
  
  // Simulated arbitrage opportunities
  const [possibleTrades, setPossibleTrades] = useState<ArbitrageOpportunity[]>([]);
  
  // Generate simulated arbitrage opportunities
  useEffect(() => {
    const generateArbitrageOpportunity = () => {
      const exchanges = ['Binance', 'Bybit', 'Kraken', 'Coinbase', 'Kucoin', 'OKX', 'Huobi'];
      const pairs = ['BTC/USDT', 'ETH/USDT', 'SOL/USDT', 'XRP/USDT', 'BNB/USDT', 'ADA/USDT', 'AVAX/USDT'];
      
      const buyExchange = exchanges[Math.floor(Math.random() * exchanges.length)];
      let sellExchange = buyExchange;
      while (sellExchange === buyExchange) {
        sellExchange = exchanges[Math.floor(Math.random() * exchanges.length)];
      }
      
      const pair = pairs[Math.floor(Math.random() * pairs.length)];
      const basePrice = pair === 'BTC/USDT' ? currentBtcPrice : 
                        pair === 'ETH/USDT' ? currentBtcPrice * 0.05 :
                        pair === 'SOL/USDT' ? currentBtcPrice * 0.001 :
                        pair === 'XRP/USDT' ? currentBtcPrice * 0.00001 :
                        pair === 'BNB/USDT' ? currentBtcPrice * 0.01 :
                        pair === 'ADA/USDT' ? currentBtcPrice * 0.00005 :
                        currentBtcPrice * 0.0005;
      
      const buyPrice = basePrice * (1 - Math.random() * 0.005);
      const sellPrice = basePrice * (1 + Math.random() * 0.005);
      const profitPercentage = ((sellPrice - buyPrice) / buyPrice) * 100;
      
      return {
        id: Date.now().toString() + Math.random().toString(36).substring(2, 9),
        buyExchange,
        sellExchange,
        pair,
        buyPrice,
        sellPrice,
        profitPercentage,
        timestamp: new Date()
      };
    };
    
    // Generate initial opportunities
    if (possibleTrades.length === 0) {
      const initialOpportunities = Array.from({ length: 5 }, (_, i) => {
        const opportunity = generateArbitrageOpportunity();
        opportunity.timestamp = new Date(Date.now() - i * 30000); // 30 seconds apart
        return opportunity;
      });
      setPossibleTrades(initialOpportunities);
    }
    
    // Add new opportunities periodically if robot is active
    const interval = setInterval(() => {
      if (robotState?.is_active) {
        setPossibleTrades(prev => {
          const newOpportunity = generateArbitrageOpportunity();
          return [newOpportunity, ...prev].slice(0, 10); // Keep only the 10 most recent
        });
      }
    }, 45000); // Every 45 seconds
    
    return () => clearInterval(interval);
  }, [robotState?.is_active, currentBtcPrice, possibleTrades.length]);
  
  // Generate simulated trading logs
  useEffect(() => {
    if (robotState?.is_active) {
      const generateLog = () => {
        const actions = ['BUY', 'SELL'];
        const exchanges = ['Binance', 'Bybit', 'Kraken', 'Coinbase'];
        const pairs = ['BTC/USDT', 'ETH/USDT', 'SOL/USDT', 'XRP/USDT'];
        
        const action = actions[Math.floor(Math.random() * actions.length)];
        const exchange = exchanges[Math.floor(Math.random() * exchanges.length)];
        const pair = pairs[Math.floor(Math.random() * pairs.length)];
        const amount = (Math.random() * 0.1).toFixed(6);
        const price = (action === 'BUY' ? currentBtcPrice * 0.9999 : currentBtcPrice * 1.0001).toFixed(2);
        const profit = (Math.random() * 0.01).toFixed(6);
        
        return {
          id: Date.now().toString(),
          action,
          pair,
          exchange,
          amount,
          price,
          profit,
          timestamp: new Date().toISOString()
        };
      };
      
      // Generate a new log every 2 minutes if robot is active
      const interval = setInterval(() => {
        const newLog = generateLog();
        setTradingLogs(prev => [newLog, ...prev].slice(0, 100));
      }, 120000);
      
      // Generate initial logs
      if (tradingLogs.length === 0) {
        const initialLogs = Array.from({ length: 10 }, (_, i) => {
          const log = generateLog();
          log.timestamp = new Date(Date.now() - i * 60000).toISOString();
          return log;
        });
        setTradingLogs(initialLogs);
      }
      
      return () => clearInterval(interval);
    }
  }, [robotState?.is_active, currentBtcPrice, tradingLogs.length]);

  // Handle robot activation
  const handleActivateRobot = async () => {
    if (robotState?.allocated_balance <= 0) {
      setError('Please allocate balance before activating the robot');
      return;
    }
    
    setIsActivating(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Update robot state
      await onUpdateRobotState({
        is_active: true,
        todays_profit: 0, // Reset daily profit on activation
        last_profit_timestamp: new Date().toISOString()
      });
      
      setSuccess('Robot activated successfully');
    } catch (error: any) {
      setError(error.message || 'Failed to activate robot');
    } finally {
      setIsActivating(false);
    }
  };

  // Handle robot deactivation
  const handleDeactivateRobot = async () => {
    setIsDeactivating(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Update robot state
      await onUpdateRobotState({
        is_active: false
      });
      
      setSuccess('Robot deactivated successfully');
    } catch (error: any) {
      setError(error.message || 'Failed to deactivate robot');
    } finally {
      setIsDeactivating(false);
    }
  };

  // Handle balance allocation
  const handleAllocateBalance = async () => {
    const amount = parseFloat(allocationAmount);
    
    if (isNaN(amount) || amount <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    
    if (amount > usdtBalance) {
      setError('Insufficient balance');
      return;
    }
    
    setIsAllocating(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Update balances
      await onUpdateBalances({
        usdt_balance: usdtBalance - amount,
        transaction: {
          type: 'robot_profit',
          amount: -amount,
          description: 'Allocated balance to WhiteRock24 Arbitrage Engine',
          status: 'completed'
        }
      });
      
      // Update robot state
      await onUpdateRobotState({
        allocated_balance: (robotState?.allocated_balance || 0) + amount
      });
      
      setSuccess(`Successfully allocated ${amount} USDT to the robot`);
      setAllocationAmount('');
      setShowAllocationForm(false);
    } catch (error: any) {
      setError(error.message || 'Failed to allocate balance');
    } finally {
      setIsAllocating(false);
    }
  };

  // Calculate daily profit percentage based on allocated balance and BTC price
  const getDailyProfitPercentage = () => {
    const allocatedBalanceInBtc = (robotState?.allocated_balance || 0) / currentBtcPrice;
    
    if (allocatedBalanceInBtc >= 50) return 2.1; // WHALE tier
    if (allocatedBalanceInBtc >= 10) return 1.2; // SHARK tier
    if (allocatedBalanceInBtc >= 5) return 0.8; // DOLPHIN tier
    if (allocatedBalanceInBtc >= 1) return 0.6; // OCTOPUS tier
    if (allocatedBalanceInBtc >= 0.1) return 0.35; // CRAB tier
    return 0.2; // SHRIMP tier
  };

  // Calculate estimated daily profit
  const estimatedDailyProfit = (robotState?.allocated_balance || 0) * (getDailyProfitPercentage() / 100);
  
  // Calculate estimated monthly profit
  const estimatedMonthlyProfit = estimatedDailyProfit * 30;
  
  // Calculate estimated yearly profit
  const estimatedYearlyProfit = estimatedDailyProfit * 365;
  
  // Get investment tier based on allocated balance
  const getInvestmentTier = () => {
    const allocatedBalanceInBtc = (robotState?.allocated_balance || 0) / currentBtcPrice;
    
    if (allocatedBalanceInBtc >= 50) return 'WHALE';
    if (allocatedBalanceInBtc >= 10) return 'SHARK';
    if (allocatedBalanceInBtc >= 5) return 'DOLPHIN';
    if (allocatedBalanceInBtc >= 1) return 'OCTOPUS';
    if (allocatedBalanceInBtc >= 0.1) return 'CRAB';
    if (allocatedBalanceInBtc > 0) return 'SHRIMP';
    return 'NONE';
  };

  // Get tier color
  const getTierColor = () => {
    const tier = getInvestmentTier();
    
    switch (tier) {
      case 'WHALE': return 'text-green-400';
      case 'SHARK': return 'text-red-400';
      case 'DOLPHIN': return 'text-blue-400';
      case 'OCTOPUS': return 'text-purple-400';
      case 'CRAB': return 'text-orange-400';
      case 'SHRIMP': return 'text-gray-400';
      default: return 'text-slate-400';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 text-white relative overflow-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl animate-pulse delay-500"></div>
      </div>

      <div className="relative z-10 p-4 md:p-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
          <div className="flex items-center gap-4">
            <div className="h-12 flex items-center">
              <img src="/image.png" alt="WhiteRock24 Logo" className="h-10" />
            </div>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-400 via-cyan-400 to-emerald-400 bg-clip-text text-transparent">
                WhiteRock24 Arbitrage Engine
              </h1>
              <p className="text-slate-400">Automated cross-exchange arbitrage trading</p>
            </div>
          </div>

          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="bg-slate-800/50 backdrop-blur-sm px-6 py-3 rounded-xl border border-slate-700/50 shadow-lg w-full md:w-auto">
              <div className="text-slate-400 text-sm">Available Balance</div>
              <div className="text-white font-mono text-xl">${usdtBalance.toFixed(2)}</div>
            </div>
            
            <div className="flex gap-3 w-full md:w-auto">
              {robotState?.is_active ? (
                <button
                  onClick={handleDeactivateRobot}
                  disabled={isDeactivating}
                  className="flex-1 md:flex-auto bg-red-600 hover:bg-red-700 disabled:bg-slate-700 text-white px-6 py-3 rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
                >
                  {isDeactivating ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <Power size={18} />
                  )}
                  Stop Robot
                </button>
              ) : (
                <button
                  onClick={handleActivateRobot}
                  disabled={isActivating || robotState?.allocated_balance <= 0}
                  className="flex-1 md:flex-auto bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 disabled:from-slate-700 disabled:to-slate-800 text-white px-6 py-3 rounded-xl font-medium transition-all duration-300 shadow-lg shadow-emerald-500/25 flex items-center justify-center gap-2"
                >
                  {isActivating ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <Power size={18} />
                  )}
                  Start Robot
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Status Messages */}
        {error && (
          <div className="mb-6 bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-center gap-3">
            <AlertTriangle size={20} className="text-red-400 flex-shrink-0" />
            <span className="text-red-400">{error}</span>
          </div>
        )}
        
        {success && (
          <div className="mb-6 bg-green-500/10 border border-green-500/30 rounded-xl p-4 flex items-center gap-3">
            <CheckCircle size={20} className="text-green-400 flex-shrink-0" />
            <span className="text-green-400">{success}</span>
          </div>
        )}

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Robot Status */}
          <div className="lg:col-span-2 space-y-6">
            {/* Robot Status Card */}
            <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-2xl">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                  <Activity size={20} className="text-blue-400" />
                  Robot Status
                </h2>
                <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                  robotState?.is_active 
                    ? 'bg-green-500/20 text-green-400' 
                    : 'bg-red-500/20 text-red-400'
                }`}>
                  {robotState?.is_active ? 'Active' : 'Inactive'}
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-slate-900/30 rounded-xl p-4 border border-slate-700/30">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center shadow-lg shadow-blue-500/25">
                      <DollarSign size={20} className="text-white" />
                    </div>
                    <div>
                      <div className="text-slate-400 text-sm">Allocated Balance</div>
                      <div className="text-white font-bold text-xl">${(robotState?.allocated_balance || 0).toFixed(2)}</div>
                    </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-3 mt-4">
                    <button
                      onClick={() => setShowAllocationForm(!showAllocationForm)}
                      className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 shadow-lg shadow-blue-500/25 flex items-center justify-center gap-2"
                    >
                      {showAllocationForm ? 'Cancel' : 'Allocate Funds'}
                    </button>
                    <button
                      onClick={() => setShowInvestmentTiers(true)}
                      className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 shadow-lg shadow-purple-500/25 flex items-center justify-center gap-2"
                    >
                      Investment Tiers
                    </button>
                  </div>
                  
                  {showAllocationForm && (
                    <div className="mt-4 bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                      <div className="mb-3">
                        <label className="block text-sm text-slate-400 mb-1">Amount to Allocate (USDT)</label>
                        <div className="flex gap-2">
                          <input
                            type="number"
                            value={allocationAmount}
                            onChange={(e) => setAllocationAmount(e.target.value)}
                            placeholder="Enter amount"
                            className="flex-1 bg-slate-900/50 text-white px-3 py-2 rounded-lg border border-slate-600/50 focus:outline-none focus:ring-1 focus:ring-blue-500/50"
                          />
                          <button
                            onClick={() => setAllocationAmount(usdtBalance.toString())}
                            className="bg-slate-700 hover:bg-slate-600 text-slate-300 px-3 py-2 rounded-lg text-xs font-medium"
                          >
                            MAX
                          </button>
                        </div>
                      </div>
                      <button
                        onClick={handleAllocateBalance}
                        disabled={isAllocating}
                        className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 disabled:from-slate-700 disabled:to-slate-800 text-white py-2 rounded-lg text-sm font-medium transition-all duration-300 shadow-lg shadow-blue-500/25 flex items-center justify-center gap-2"
                      >
                        {isAllocating ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          'Confirm Allocation'
                        )}
                      </button>
                    </div>
                  )}
                </div>
                
                <div className="bg-slate-900/30 rounded-xl p-4 border border-slate-700/30">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-emerald-500 to-green-500 rounded-lg flex items-center justify-center shadow-lg shadow-emerald-500/25">
                      <TrendingUp size={20} className="text-white" />
                    </div>
                    <div>
                      <div className="text-slate-400 text-sm">Today's Profit</div>
                      <div className="text-emerald-400 font-bold text-xl">+${(robotState?.todays_profit || 0).toFixed(2)}</div>
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-400">Investment Tier</span>
                      <span className={`font-medium ${getTierColor()}`}>{getInvestmentTier()}</span>
                    </div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-400">Daily Profit Rate</span>
                      <span className="text-emerald-400">{getDailyProfitPercentage()}%</span>
                    </div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-400">Total Trades</span>
                      <span className="text-white">{robotState?.total_trades || 0}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Success Rate</span>
                      <span className="text-white">
                        {robotState?.total_trades > 0 
                          ? ((robotState?.successful_trades / robotState?.total_trades) * 100).toFixed(1) 
                          : 0}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Profit Projections */}
              <div className="mt-6 bg-slate-900/30 rounded-xl p-4 border border-slate-700/30">
                <h3 className="text-lg font-semibold text-white mb-4">Profit Projections</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
                    <div className="text-slate-400 text-sm mb-1">Daily</div>
                    <div className="text-emerald-400 font-bold text-lg">+${estimatedDailyProfit.toFixed(2)}</div>
                    <div className="text-xs text-slate-500">{getDailyProfitPercentage()}% of allocated balance</div>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
                    <div className="text-slate-400 text-sm mb-1">Monthly</div>
                    <div className="text-emerald-400 font-bold text-lg">+${estimatedMonthlyProfit.toFixed(2)}</div>
                    <div className="text-xs text-slate-500">{(getDailyProfitPercentage() * 30).toFixed(1)}% of allocated balance</div>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
                    <div className="text-slate-400 text-sm mb-1">Yearly</div>
                    <div className="text-emerald-400 font-bold text-lg">+${estimatedYearlyProfit.toFixed(2)}</div>
                    <div className="text-xs text-slate-500">{(getDailyProfitPercentage() * 365).toFixed(1)}% of allocated balance</div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Possible Arbitrage Opportunities */}
            <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-2xl">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                  <Repeat size={20} className="text-blue-400" />
                  Possible Arbitrage Opportunities
                </h2>
                <div className="text-xs text-slate-400 bg-slate-700/50 px-3 py-1 rounded-full">
                  {possibleTrades.length} Opportunities
                </div>
              </div>
              
              {possibleTrades.length > 0 ? (
                <div className="space-y-3 mb-6">
                  {possibleTrades.map((trade) => (
                    <div key={trade.id} className="bg-slate-800/40 rounded-xl p-4 border border-slate-700/40 hover:border-blue-500/30 hover:bg-slate-800/60 transition-all duration-300 shadow-md">
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-white font-medium text-lg">{trade.pair}</span>
                          <span className="text-xs px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-400 font-bold">
                            +{trade.profitPercentage.toFixed(2)}%
                          </span>
                        </div>
                        <span className="text-xs text-slate-400 bg-slate-700/40 px-2 py-1 rounded-lg">
                          {new Date(trade.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm mt-2">
                        <div className="flex items-center gap-2 bg-slate-900/40 px-3 py-2 rounded-lg">
                          <ArrowRight size={14} className="text-green-400" />
                          <span className="text-slate-300">
                            Buy: <span className="text-white font-medium">{trade.buyExchange}</span>
                          </span>
                        </div>
                        <div className="flex items-center gap-2 bg-slate-900/40 px-3 py-2 rounded-lg">
                          <ArrowRight size={14} className="text-red-400" />
                          <span className="text-slate-300">
                            Sell: <span className="text-white font-medium">{trade.sellExchange}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-slate-500 mb-6">
                  <p className="mb-2">No arbitrage opportunities found</p>
                  <p className="text-sm text-slate-600">Activate the robot to start scanning for opportunities</p>
                </div>
              )}
              
              {/* Lottie Animation */}
              <div className="w-full flex justify-center mt-6">
                <div className="w-64 h-64 bg-slate-800/30 rounded-full p-4 flex items-center justify-center">
                  <Lottie 
                    path="https://lottie.host/b2c6b06e-c1be-43d9-aa72-80e337c75521/ndeW7TxtpO.lottie"
                    loop={true} 
                    className="w-full h-full"
                  />
                </div>
              </div>
            </div>
            
            {/* Trading Logs */}
            <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                  <BarChart3 size={20} className="text-blue-400" />
                  Trading Logs
                </h2>
                <button 
                  onClick={() => setShowTradingLogs(!showTradingLogs)}
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  {showTradingLogs ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
              </div>
              
              {showTradingLogs ? (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {tradingLogs.length > 0 ? (
                    tradingLogs.map((log) => (
                      <div key={log.id} className="bg-slate-900/30 rounded-lg p-3 border border-slate-700/30 hover:border-slate-600/50 transition-all duration-300">
                        <div className="flex justify-between items-center mb-1">
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              log.action === 'BUY' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
                            }`}>
                              {log.action}
                            </span>
                            <span className="text-white font-medium">{log.pair}</span>
                          </div>
                          <span className="text-xs text-slate-400">
                            {new Date(log.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-400">{log.exchange} • {log.amount} BTC @ ${log.price}</span>
                          <span className="text-emerald-400">+${log.profit}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <Activity size={48} className="mx-auto mb-4 opacity-50" />
                      <p className="mb-2">No trading activity yet</p>
                      <p className="text-sm text-slate-600">Activate the robot to start trading</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-6 text-slate-400">
                  Click to view trading logs
                </div>
              )}
            </div>
          </div>
          
          {/* Right Column - How It Works & Settings */}
          <div className="space-y-8">
            {/* How It Works */}
            <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-2xl">
              <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                <Info size={20} className="text-blue-400" />
                How It Works
              </h2>
              
              <div className="space-y-4">
                <div className="bg-slate-900/30 rounded-xl p-4 border border-slate-700/30">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center shadow-lg shadow-blue-500/25 text-white font-bold">
                      1
                    </div>
                    <h3 className="text-lg font-medium text-white">Market Analysis</h3>
                  </div>
                  <p className="text-slate-400 text-sm">
                    Our AI constantly monitors price differences across 15+ exchanges to identify profitable arbitrage opportunities.
                  </p>
                </div>
                
                <div className="bg-slate-900/30 rounded-xl p-4 border border-slate-700/30">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center shadow-lg shadow-purple-500/25 text-white font-bold">
                      2
                    </div>
                    <h3 className="text-lg font-medium text-white">Opportunity Detection</h3>
                  </div>
                  <p className="text-slate-400 text-sm">
                    When price discrepancies exceed your profit threshold, the system identifies a potential trade.
                  </p>
                </div>
                
                <div className="bg-slate-900/30 rounded-xl p-4 border border-slate-700/30">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 bg-gradient-to-r from-emerald-500 to-green-500 rounded-lg flex items-center justify-center shadow-lg shadow-emerald-500/25 text-white font-bold">
                      3
                    </div>
                    <h3 className="text-lg font-medium text-white">Automated Execution</h3>
                  </div>
                  <p className="text-slate-400 text-sm">
                    The robot executes trades across exchanges in milliseconds, capturing profits before the opportunity disappears.
                  </p>
                </div>
                
                <div className="bg-slate-900/30 rounded-xl p-4 border border-slate-700/30">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 bg-gradient-to-r from-amber-500 to-orange-500 rounded-lg flex items-center justify-center shadow-lg shadow-amber-500/25 text-white font-bold">
                      4
                    </div>
                    <h3 className="text-lg font-medium text-white">Profit Accumulation</h3>
                  </div>
                  <p className="text-slate-400 text-sm">
                    Profits are automatically added to your balance daily, with higher investment tiers earning greater returns.
                  </p>
                </div>
              </div>
              
              <button
                onClick={() => setShowInvestmentTiers(true)}
                className="w-full mt-6 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg shadow-blue-500/20 flex items-center justify-center gap-2"
              >
                View Investment Tiers
                <ArrowRight size={18} />
              </button>
            </div>
            
            {/* Robot Settings */}
            <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-2xl">
              <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                <Settings size={20} className="text-blue-400" />
                Robot Settings
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Trading Strategy</label>
                  <select
                    value={robotState?.strategy || 'triangular'}
                    onChange={(e) => onUpdateRobotState({ strategy: e.target.value })}
                    className="w-full bg-slate-900/50 text-white px-4 py-3 rounded-xl border border-slate-600/50 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
                  >
                    <option value="triangular">Triangular Arbitrage</option>
                    <option value="spatial">Spatial Arbitrage</option>
                    <option value="statistical">Statistical Arbitrage</option>
                    <option value="latency">Latency Arbitrage</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm text-slate-400 mb-2">
                    Minimum Profit Threshold (%)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.1"
                    max="5"
                    value={robotState?.min_profit_threshold || 0.5}
                    onChange={(e) => onUpdateRobotState({ min_profit_threshold: parseFloat(e.target.value) })}
                    className="w-full bg-slate-900/50 text-white px-4 py-3 rounded-xl border border-slate-600/50 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
                  />
                  <p className="text-xs text-slate-500 mt-1 ml-1">
                    Minimum profit percentage required to execute a trade (0.1% - 5%)
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm text-slate-400 mb-2">
                    Maximum Trade Amount (USDT)
                  </label>
                  <input
                    type="number"
                    step="10"
                    min="10"
                    max={robotState?.allocated_balance || 1000}
                    value={robotState?.max_trade_amount || 1000}
                    onChange={(e) => onUpdateRobotState({ max_trade_amount: parseFloat(e.target.value) })}
                    className="w-full bg-slate-900/50 text-white px-4 py-3 rounded-xl border border-slate-600/50 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
                  />
                  <p className="text-xs text-slate-500 mt-1 ml-1">
                    Maximum amount per trade (10 - {robotState?.allocated_balance || 1000} USDT)
                  </p>
                </div>
              </div>
              
              <button
                onClick={() => onUpdateRobotState({ 
                  strategy: robotState?.strategy || 'triangular',
                  min_profit_threshold: robotState?.min_profit_threshold || 0.5,
                  max_trade_amount: robotState?.max_trade_amount || 1000
                })}
                className="w-full mt-6 bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white py-3 rounded-xl font-medium transition-all duration-300 flex items-center justify-center gap-2"
              >
                <Settings size={18} />
                Save Settings
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Investment Tiers Modal */}
      {showInvestmentTiers && (
        <InvestmentTiersModal
          isOpen={showInvestmentTiers}
          onClose={() => setShowInvestmentTiers(false)}
          onInvestNow={() => setShowAllocationForm(true)}
        />
      )}
    </div>
  );
};

export default ArbitrageRobotPage;